import type { DisputeApi, DisputeDetailApi, DisputesSummaryApi } from "@/types/api/disputes";
import type { DisputeUI, DisputesKpisUI } from "@/types/ui/disputes";
import type { TransactionType } from "@/types/transaction";

export function mapDisputeApiToUi(apiDispute: DisputeApi): DisputeUI {
  return {
    id: apiDispute.disputeNumber,
    numericId: parseInt(apiDispute.id, 10),
    customer: apiDispute.customer,
    customerId: apiDispute.customerId,
    customerAccountCode: apiDispute.customerAccountCode ?? "",
    contactId: apiDispute.contactId,
    contactName: apiDispute.contactName ?? "",
    contactEmail: apiDispute.contactEmail ?? "",
    contactPhone: apiDispute.contactPhone ?? "",
    type: apiDispute.type,
    amount: apiDispute.amount,
    status: apiDispute.status,
    priority: apiDispute.priority,
    assignedTo: apiDispute.assignedTo ?? "",
    createdDate: apiDispute.createdDate,
    dueDate: apiDispute.dueDate ?? "",
    workflow: apiDispute.workflow ?? "",
    aiScore: apiDispute.aiScore ?? null,
    aiRecommendation: apiDispute.aiRecommendation ?? "",
    currentLevel: apiDispute.currentLevel,
    totalLevels: apiDispute.totalLevels,
    description: apiDispute.description ?? "",
    resolutionNotes: apiDispute.resolutionNotes ?? "",
    closedReason: (apiDispute.closedReason as DisputeUI['closedReason']) ?? null,
    closedDate: apiDispute.closedDate ?? null,
    closedNote: apiDispute.closedNote ?? null,
    // Resolution outcome fields
    resolutionOutcome: (apiDispute.resolutionOutcome as DisputeUI['resolutionOutcome']) ?? null,
    resolutionAmount: apiDispute.resolutionAmount ?? null,
    resolvedBy: apiDispute.resolvedByUserName ?? null,
    resolvedAtLevel: apiDispute.resolvedAtLevel ?? undefined,
  };
}

function normaliseTransactionTypeFromApi(typeName: string): TransactionType {
  const map: Record<string, TransactionType> = {
    'Invoice': 'invoice',
    'Credit Note': 'credit_note',
    'Receipt': 'receipt',
    'Debit Adjustment': 'debit_adjustment',
    'Credit Adjustment': 'credit_adjustment',
  };
  return map[typeName] ?? 'invoice';
}

function mapTransactionStatus(status: string): 'outstanding' | 'overdue' | 'completed' {
  if (status === 'Overdue') return 'overdue';
  if (status === 'Paid') return 'completed';
  return 'outstanding';
}

export function mapDisputeDetailApiToUi(apiDispute: DisputeDetailApi): DisputeUI {
  return {
    id: apiDispute.disputeNumber,
    numericId: parseInt(apiDispute.id, 10),
    customer: apiDispute.customer,
    customerId: apiDispute.customerId,
    customerAccountCode: "",
    contactId: apiDispute.contactId,
    contactName: apiDispute.contactName ?? "",
    contactEmail: apiDispute.contactEmail ?? "",
    contactPhone: apiDispute.contactPhone ?? "",
    type: apiDispute.type,
    amount: apiDispute.amount,
    status: apiDispute.status,
    priority: apiDispute.priority,
    assignedTo: apiDispute.assignedTo ?? "",
    createdDate: apiDispute.createdDate,
    dueDate: apiDispute.dueDate ?? "",
    workflow: apiDispute.workflow ?? "",
    aiScore: apiDispute.aiScore ?? null,
    aiRecommendation: apiDispute.aiRecommendation ?? "",
    currentLevel: apiDispute.currentLevel,
    totalLevels: apiDispute.totalLevels,
    description: apiDispute.description ?? "",
    resolutionNotes: apiDispute.resolutionNotes ?? "",
    closedReason: (apiDispute.closedReason as DisputeUI['closedReason']) ?? null,
    closedDate: apiDispute.closedDate ?? null,
    closedNote: apiDispute.closedNote ?? null,
    resolutionOutcome: (apiDispute.resolutionOutcome as DisputeUI['resolutionOutcome']) ?? null,
    resolutionAmount: apiDispute.resolutionAmount ?? null,
    resolvedBy: apiDispute.resolvedByUserName ?? null,
    resolvedAtLevel: apiDispute.resolvedAtLevel ?? undefined,
    transactions: apiDispute.transactions?.map(x => ({
      id: x.id.toString(),
      referenceNumber: x.referenceNumber,
      date: x.transactionDate,
      dueDate: x.dueDate ?? "",
      description: x.description ?? "",
      transactionType: normaliseTransactionTypeFromApi(x.transactionType),
      amount: x.amount,
      currency: x.currency,
      status: mapTransactionStatus(x.status),
    })),
  };
}

export function mapSummaryToKpis(summary: DisputesSummaryApi): DisputesKpisUI {
  return {
    totalDisputes: summary.totalDisputes,
    totalAmount: summary.totalAmount,
    newCount: summary.newCount,
    inProgressCount: summary.inProgressCount,
    resolvedCount: summary.resolvedCount,
    highPriorityCount: summary.highPriorityCount,
  };
}
